import type { IHostViewportArea } from '../useHostViewport';

/**
 * Where the passage card sits beside the answer.
 *
 * The card is laid over the sidebar's column rather than placed in it, so nothing there moves, level
 * with the passage it describes. It belongs to the passage, so it scrolls with it.
 *
 * Every distance here is in pixels from the top edge of the answer's grid.
 */

/** The space kept between the card and the edges of the visible page. */
export const PASSAGE_CARD_VIEWPORT_MARGIN = 8;

/** How far the card always reaches into the passage's height, so it never drifts off beside another. */
export const PASSAGE_CARD_MIN_OVERLAP = 16;

/** What the card is placed against, measured in the grid's frame. */
export interface PassageCardFrame {
  passageTop: number;
  passageBottom: number;
  cardHeight: number;
  /** The grid's own height: the card stays inside it. */
  gridHeight: number;
  /** The part of the grid inside the visible page, which may reach beyond the grid at either end. */
  viewTop: number;
  viewBottom: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * The card's top beside the answer: level with the passage, moved up or down only as far as it takes
 * to stay on the visible page — never so far that it stops overlapping the passage's height, and
 * never outside the grid.
 */
export function passageCardTop(frame: PassageCardFrame): number {
  const { passageTop, passageBottom, cardHeight, gridHeight, viewTop, viewBottom } = frame;
  const visibleTop = viewTop + PASSAGE_CARD_VIEWPORT_MARGIN;
  const visibleBottom = viewBottom - PASSAGE_CARD_VIEWPORT_MARGIN;

  // On the visible page: a card that fits is held between its edges; a taller one starts at its top.
  let top =
    cardHeight <= visibleBottom - visibleTop ? clamp(passageTop, visibleTop, visibleBottom - cardHeight) : Math.max(passageTop, visibleTop);

  // Beside the passage: once the passage scrolls away, the card goes with it.
  const overlap = Math.min(PASSAGE_CARD_MIN_OVERLAP, (passageBottom - passageTop) / 2, cardHeight / 2);
  top = clamp(top, passageTop - cardHeight + overlap, passageBottom - overlap);

  // Inside the grid, whatever the rest asks for.
  return Math.round(clamp(top, 0, Math.max(0, gridHeight - cardHeight)));
}

/**
 * The visible page, measured from the grid's top edge (`gridTop`, the grid's distance from the frame's
 * top): the frame's own viewport, or, where the host page scrolls a frame sized to its content, the
 * band of the frame the host reports in view (`host`), since the frame's own viewport is then the whole
 * content. It starts below the area's pinned parts, which cover the top `pinnedHeight` of either.
 */
export function passageCardView({
  gridTop,
  windowHeight,
  host,
  pinnedHeight,
}: {
  gridTop: number;
  windowHeight: number;
  host?: IHostViewportArea;
  pinnedHeight: number;
}): Pick<PassageCardFrame, 'viewTop' | 'viewBottom'> {
  if (host) {
    return { viewTop: host.top + pinnedHeight - gridTop, viewBottom: host.top + host.height - gridTop };
  }
  return { viewTop: pinnedHeight - gridTop, viewBottom: windowHeight - gridTop };
}
